/**
 * The six layers an important business service rests on.
 *
 * Vendor question 4.1, and the question the whole regime is built
 * around: not "is this service approved and within tolerance?" -- the
 * story hub above already answers that -- but "what does it actually
 * rest on?".
 *
 * THE POINT OF THIS SURFACE IS THE ABSENCES. A layer nobody has mapped
 * is stated as unmapped rather than rendered as an empty space that
 * reads like a clean bill of health, and it is kept distinct from the
 * two things that look identical in a list and mean opposite things:
 * a layer that CANNOT resolve because an upstream one is empty, and a
 * layer the bank has DECLARED it does not have (a back-office service
 * has no customer journey, and saying "unmapped" there would invent a
 * finding).
 *
 * Data reads differently on purpose. It is a free-text classification
 * on each service rather than a modelled asset, so residency and
 * data-impact are unanswerable, and the layer says so instead of
 * showing a tidy value that implies otherwise.
 */
import { said } from "shared/i18n/said";
import { useTranslation } from "react-i18next";
import { InfoTip } from "shared/components/Explainer";
import { layerDetailLabel } from "shared/vocab/workflowState";
import { useState } from "react";
import { Link } from "react-router-dom";

import { PathTrail } from "shared/components/PathTrail";
import { layerItemPath } from "shared/routes/entityRoutes";

import type { DependencyLayer } from "shared/api/types";
import { useIBSDependencyLayers, useIBSStory } from "twin/api/hooks";
import { useMyEstate } from "twin/home/useMyEstate";

type Tone = "green" | "amber" | "slate" | "muted";

const REACH_TONE: Record<string, Tone> = {
  direct: "green",
  transitive: "green",
  absent: "amber",
  unreachable: "slate",
  not_applicable: "slate",
};

const TONE_CLASS: Record<Tone, string> = {
  green: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  slate: "border-slate-200 bg-slate-50 text-slate-600",
  muted: "border-slate-200 bg-white text-slate-500",
};

function LayerRow({
  layer,
  onShowAll,
  owner,
}: {
  layer: DependencyLayer;
  onShowAll: () => void;
  /** A2 -- the same block, read by the man who cannot do most of the
   *  mapping. He met six chain expressions, five layer names he has
   *  never used and "the regime" as the authority, on a page kept
   *  deliberately short for him. */
  owner: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const tone: Tone = layer.modelled ? (REACH_TONE[layer.reach] ?? "slate") : "muted";

  return (
    <li
      data-testid="dependency-layer"
      data-layer={layer.layer}
      data-reach={layer.reach}
      data-gap={layer.is_gap ? "true" : "false"}
      className="rounded border border-slate-200 bg-white px-3 py-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-slate-900">
            {t(`modules.twin.layers.name.${layer.layer}`)}
          </span>
          <span
            data-testid="dependency-layer-reach"
            className={`rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${TONE_CLASS[tone]}`}
          >
            {t(`modules.twin.layers.reach.${layer.reach}`)}
          </span>
        </span>
        {layer.item_total > 0 && (
          <button
            type="button"
            data-testid="dependency-layer-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-xs font-medium text-brand-brassDark hover:underline"
          >
            {open
              ? t("common.hide")
              : t("modules.twin.layers.show", { count: layer.item_total })}
          </button>
        )}
      </div>

      {/* The join walked, so a reader can check the claim rather than
          take it on trust -- three of the six are only reachable
          THROUGH the services and that is worth seeing.

          Behind a disclosure for the owner: the trail from business
          service to data asset to location is how a data steward checks our working,
          and it is six lines of schema to the person whose page this
          is. */}
      {owner ? (
        <details className="mt-1">
          <summary
            data-testid="dependency-layer-path-toggle"
            className="cursor-pointer text-[11px] text-slate-500"
          >
            {t("modules.twin.layers.howReached")}
          </summary>
          <PathTrail
            hops={layer.path_hops ?? []}
            keyPrefix="modules.twin.layers.hop"
            fallback={layer.path}
            testid="dependency-layer-path"
            className="mt-0.5 text-[11px] text-slate-500"
          />
        </details>
      ) : (
        <PathTrail
          hops={layer.path_hops ?? []}
          keyPrefix="modules.twin.layers.hop"
          fallback={layer.path}
          testid="dependency-layer-path"
          className="mt-1 text-[11px] text-slate-500"
        />
      )}

      {/* A2 -- for the owner, an unmapped layer says what is missing
          and WHOSE it is. "not mapped" tells him there is a hole and
          nothing about who fills it. */}
      {owner && layer.is_gap && (
        <p
          data-testid="dependency-layer-owner-gap"
          className="mt-1 text-[11px] text-amber-800"
        >
          {t(`modules.twin.layers.ownerGap.${layer.layer}`, { defaultValue: "" })}
        </p>
      )}

      {layer.note && (
        <p
          data-testid="dependency-layer-note"
          className={`mt-1 text-[11px] ${layer.is_gap ? "text-amber-800" : "text-slate-500"}`}
        >
          {layer.note_key ? said(t, layer.note_key, layer.note) : layer.note}
        </p>
      )}

      {open && layer.items.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {layer.items.map((item) => {
            /* A2 -- the row names a thing, so it opens that thing.
               It rendered "SARIE Gatewayserviceibs_supporting": the
               name, then our column value, then another column
               value, none of them a link. A reader shown the service
               their IBS rests on could not reach it. */
            const href = item.id ? layerItemPath(item.kind, item.id) : null;
            return (
              <li
                key={`${item.kind}-${item.id ?? item.name}`}
                data-testid="dependency-layer-item"
                className="text-xs text-slate-700"
              >
                {href ? (
                  <Link
                    to={href}
                    data-testid="dependency-layer-item-link"
                    className="font-medium text-brand-brassDark hover:underline"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <span className="font-medium">{item.name}</span>
                )}
                {/* Round 11 F3 -- the kind in the reader's words, then
                    the qualifiers in theirs, then the bank's own text,
                    each separated by a CHARACTER rather than a margin.
                    `ms-2` separates them on screen and not in
                    innerText, which is what a copy into a board pack
                    and a screen reader both get: the row read
                    "SARIE Gatewaya supporting serviceibs_supporting",
                    and the component's own comment predicted it. */}
                {[
                  t(`modules.twin.layers.kind.${item.kind}`, {
                    defaultValue: item.kind.replace(/_/g, " "),
                  }),
                  ...(item.detail_terms ?? []).map(layerDetailLabel),
                ].map((word) => (
                  <span key={word} className="text-slate-500">
                    <span aria-hidden="true"> · </span>
                    {word}
                  </span>
                ))}
                {item.detail && (
                  <span className="text-slate-500">
                    <span aria-hidden="true"> · </span>
                    {/* The bank's own words -- a CMDB class, a country
                        list -- so verbatim, and direction-detected. */}
                    <span dir="auto">{item.detail}</span>
                  </span>
                )}
              </li>
            );
          })}
          {layer.truncated && (
            <li
              data-testid="dependency-layer-truncated"
              className="text-xs text-slate-500"
            >
              {t("modules.twin.layers.truncated", {
                shown: layer.items.length,
                total: layer.item_total,
              })}{" "}
              <button
                type="button"
                data-testid="dependency-layer-show-rest"
                onClick={onShowAll}
                className="font-medium text-brand-brassDark hover:underline"
              >
                {t("modules.twin.layers.showRest", {
                  count: layer.item_total - layer.items.length,
                })}
              </button>
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

export function IBSDependencyLayers({
  ibsId,
}: {
  ibsId: string | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const estate = useMyEstate();
  const owner = (estate.data?.ibses ?? []).some((i) => i.id === ibsId);
  const q = useIBSDependencyLayers(ibsId, showAll);
  // Round 16 -- a Critical Internal Function is not "this service". The
  // story hub above has already fetched the class; this is a cache read.
  const internal = useIBSStory(ibsId).data?.service_class === "internal";
  if (!q.data) return null;
  const stack = q.data;
  /* A2 -- "5 of 6 layers resolve" counted a layer the screen itself
     labels "none by design" as a shortfall, which invites exactly the
     question the standfirst was written to prevent. Accounted-for is
     the honest headline: mapped, plus declared-absent. The fallbacks
     keep an older payload rendering the old number rather than NaN. */
  const total = stack.layers.length;
  const byDesign = stack.by_design_count ?? 0;
  const accounted = stack.accounted_count ?? stack.resolved_count;

  return (
    <section id="dependencies" data-testid="dependency-layers" className="mt-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
        {t(
          internal
            ? "modules.twin.layers.headingInternal"
            : "modules.twin.layers.heading",
        )}
        {/* ROUND 13 H6, fourth round -- the construct, where it is
            met. "The six layers" is this product's own invention and
            the glossary had no entry for it, so a reader meeting six
            named dimensions for the first time had nowhere to look. */}
        <InfoTip termKey="dependency_layers" />
      </h2>
      <p className="mt-1 text-xs text-slate-600">
        {t(
          internal ? "modules.twin.layers.blurbInternal" : "modules.twin.layers.blurb",
        )}
      </p>
      {/* Round 10 C2 -- what a layer is FOR, which is what makes an
          unmapped one worth a reader's attention. */}
      <p data-testid="dependency-layers-why" className="mt-1 text-xs text-slate-500">
        {t("modules.twin.layers.whyLayers")}
      </p>
      {/* Which of the six are his. Without it the block reads as six
          things he is failing to do, when one of them is his. */}
      {owner && (
        <p
          data-testid="dependency-layers-owner-line"
          className="mt-1 text-xs text-slate-700"
        >
          {t("modules.twin.layers.ownerLine")}
        </p>
      )}
      <p
        data-testid="dependency-layers-summary"
        data-resolved={stack.resolved_count}
        data-gaps={stack.gap_layers.length}
        className="mt-1 text-xs text-slate-500"
      >
        {t("modules.twin.layers.summary", {
          accounted,
          total,
          mapped: stack.resolved_count,
          byDesign,
        })}{" "}
        {stack.gap_layers.length > 0 && (
          <span className="text-amber-800">
            {t("modules.twin.layers.gaps", {
              layers: stack.gap_layers
                .map((l) => t(`modules.twin.layers.name.${l}`))
                .join(", "),
            })}
          </span>
        )}
      </p>
      {/* ROUND 13 G5 -- whose clock this is. The count is computed on
          read and moves the instant a dependency is named; the nearest
          timestamp on the page belonged to the sealed obligation
          snapshot below, so a reader watched the number change under a
          footer stamped before he arrived. */}
      <p
        data-testid="dependency-layers-live"
        className="mt-1 text-[11px] text-slate-400"
      >
        {t("modules.twin.layers.liveNow")}
      </p>
      <ul className="mt-2 space-y-1.5">
        {stack.layers.map((layer) => (
          <LayerRow
            key={layer.layer}
            layer={layer}
            onShowAll={() => setShowAll(true)}
            owner={owner}
          />
        ))}
      </ul>
    </section>
  );
}
